<template>
  <CModal
    v-model:visible="showModal"
    centered
    size="sm"
    @close="closeModal"
  >
    <template #header-wrapper>
      <div class="modal-header-custom p-3 border-bottom text-center">
        <i class="fas fa-cloud-upload-alt fa-2x text-primary mb-2" />
        <h4 class="font-weight-bold mb-0">
          Upload OTA Firmware
        </h4>
      </div>
    </template>

    <CCardBody class="p-4">
      <div v-if="!isUploading">
        <div class="mb-4">
          <label class="font-weight-bold small text-uppercase text-muted">Version Details</label>
          <CInput
            v-model="version"
            placeholder="e.g. 1.0.2"
          >
            <template #prepend-content>
              <i class="fas fa-tag" />
            </template>
          </CInput>
        </div>

        <div class="mb-3">
          <label class="font-weight-bold small text-uppercase text-muted">
            Firmware Binary <span class="text-info">({{ allowedExtensions.join(', ') }})</span>
          </label>
          <div 
            class="file-drop-zone"
            :class="{
              'file-selected': selectedFile,
              'drag-over': isDragging,
              'border-danger': extensionError
            }"
            @click="triggerFileSelect"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleFileDrop"
          >
            <div
              v-if="!selectedFile"
              class="text-center py-3"
            >
              <i
                class="fas fa-file-upload fa-2x mb-2"
                :class="extensionError ? 'text-danger' : 'text-secondary'"
              />
              <p
                class="mb-0 small"
                :class="{'text-danger': extensionError}"
              >
                {{ extensionError ? 'Invalid file type' : 'Click or drag firmware here' }}
              </p>
            </div>
            <div
              v-else
              class="text-center py-2 w-100"
            >
              <i class="fas fa-file-archive fa-2x mb-2 text-success" />
              <p
                class="mb-1 font-weight-bold text-truncate d-block mx-auto px-3" 
                style="max-width: 250px;" 
                :title="selectedFile.name"
              >
                {{ selectedFile.name }}
              </p>
              <span class="badge badge-light border">{{ formatBytes(selectedFile.size) }}</span>
            </div>
          </div>
          
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            :accept="allowedExtensions.join(',')"
            @change="handleFileSelect"
          >
        </div>
      </div>

      <div
        v-else
        class="text-center py-4"
      >
        <div class="icon-pulse mb-3">
          <i class="fas fa-cloud-upload-alt fa-3x text-primary" />
        </div>
        <h5 class="mb-1">
          Uploading version {{ version }}...
        </h5>
        <CProgress
          :value="uploadProgress"
          color="success"
          striped
          animated
          class="mb-2"
          style="height: 10px;"
        />
        <span class="font-weight-bold text-success h4">{{ uploadProgress }}%</span>
      </div>
    </CCardBody>

    <template #footer-wrapper>
      <div class="p-3 border-top d-flex justify-content-between bg-light">
        <CButton
          color="secondary"
          :disabled="isUploading"
          @click="closeModal"
        >
          CANCEL
        </CButton>
        <CButton 
          color="primary" 
          :disabled="!selectedFile || !version.trim() || isUploading" 
          @click="startUpload"
        >
          <i class="fas fa-cloud-upload-alt mr-1" /> START UPLOAD
        </CButton>
      </div>
    </template>
  </CModal>
</template>

<script>
import axios from 'axios'; 

export default {
  name: "UploadFirmwareModal",
  props: {
    firmwareId: { type: Number, default: null },
    allowedExtensions: {
      type: Array,
      default: () => ['.bin', '.zip', '.hex', '.tar.gz']
    }
  },
  emits: ['saved'],
  data() {
    return {
      showModal: false,
      selectedFile: null,
      version: '', 
      isUploading: false,
      uploadProgress: 0,
      isDragging: false,
      extensionError: false
    }
  },
  methods: {
    open() {
      this.resetData();
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.resetData();
    },

    resetData() {
      this.selectedFile = null;
      this.version = ''; 
      this.isUploading = false;
      this.uploadProgress = 0;
      this.isDragging = false;
      this.extensionError = false;
    },

    triggerFileSelect() {
      const input = this.$refs.fileInput;
      if (input) {
        input.value = '';
        input.click();
      }
    },

    isValidFile(file) {
      if (!file) return false;
      const fileName = file.name.toLowerCase();
      return this.allowedExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
    },

    processFile(file) {
      this.extensionError = false;
      if (this.isValidFile(file)) {
        this.selectedFile = file;
      } else {
        this.selectedFile = null;
        this.extensionError = true;
        this.$toast.warning(`Invalid file. Please use: ${this.allowedExtensions.join(', ')}`);
      }
    },

    handleFileSelect(event) {
      const files = event.target ? event.target.files : event;
      if (files && files.length > 0) {
        this.processFile(files[0]);
      }
    },

    handleFileDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        this.processFile(files[0]);
      }
    },

    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },

    async startUpload() {
      if (!this.selectedFile || !this.firmwareId || !this.version.trim()) return;

      this.isUploading = true;
      this.uploadProgress = 0;

      try {
        const strictContentType = this.selectedFile.type || 'application/octet-stream';
        
        const ticketResponse = await this.$http.post(`/firmwares/${this.firmwareId}/files/ticket`, {
          filename: this.selectedFile.name,
          content_type: strictContentType,
          version: this.version.trim() 
        });

        const { upload_url, file_id } = ticketResponse.data;

        const cleanAxios = axios.create();
        await cleanAxios.put(upload_url, this.selectedFile, {
          headers: { 'Content-Type': strictContentType },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          }
        });

        await this.$http.post(`/firmware-files/${file_id}/confirm`);

        this.$emit('saved');
        this.closeModal();

      } catch (error) {
        console.error('Upload failed:', error);
        this.$toast.error("Error during upload process");
        this.isUploading = false;
      }
    }
  }
}
</script>

<style scoped>
.file-drop-zone {
  border: 2px dashed #d8dbe0;
  border-radius: 8px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.file-drop-zone:hover {
  background-color: #f0f3f5;
  border-color: #321fdb;
}
.file-drop-zone.drag-over {
  border-color: #2eb85c;
  background-color: #ebf9f1;
  transform: scale(1.02);
}
.file-drop-zone.file-selected {
  border: 2px solid #2eb85c;
  background-color: #f0fff4;
}
.file-drop-zone.border-danger {
  border-color: #e55353 !important;
  background-color: #fff5f5;
}
@keyframes pulse-blue {
  0% { transform: scale(0.95); opacity: 0.7; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.7; }
}
.icon-pulse {
  animation: pulse-blue 2s infinite ease-in-out;
}
</style>
